import { Controller } from "@hotwired/stimulus";
import { v4 as uuid } from "uuid";

export default class extends Controller<HTMLFieldSetElement> {
  static targets = [
    "target",
    "template",
    "newLabelForm",
    "newLabelError",
    "newLabelButton",
    "labelList",
    "infoPane",
  ];
  declare templateTarget: HTMLElement;
  declare targetTarget: HTMLElement;
  declare newLabelFormTarget: HTMLFormElement;
  declare newLabelErrorTarget: HTMLParagraphElement;
  declare newLabelButtonTarget: HTMLButtonElement;
  declare labelListTarget: HTMLElement;
  declare infoPaneTarget: HTMLElement;

  add(evt: Event): void {
    evt.preventDefault();

    const content: string = this.templateTarget.innerHTML.replace(/RECORD_REF/g, uuid());

    this.targetTarget.insertAdjacentHTML("beforebegin", content);
  }

  remove(evt: Event): void {
    evt.preventDefault();

    if (!(evt.target instanceof HTMLElement)) {
      return;
    }

    const wrapper: HTMLElement | null = evt.target.closest("[data-wrapper]");
    if (!wrapper) {
      return;
    }

    const inputs: NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> =
      wrapper.querySelectorAll('input:not([type="hidden"]), textarea, select');
    inputs.forEach((input) => {
      input.disabled = true;
    });

    if (wrapper.dataset.newRecord === "true") {
      wrapper.remove();
    } else {
      wrapper.style.display = "none";

      const input: HTMLInputElement | null = wrapper?.querySelector("input[name*='_destroy']");
      if (input) {
        input.value = "1";
      }
    }
  }

  onMonitorChange(monitorEvt: CustomEvent): void {
    const monitor = monitorEvt.detail.monitor;

    if (monitor === "passed_on_retry") {
      this.infoPaneTarget.classList.remove("hidden");
      this.element.disabled = true;
    } else {
      this.infoPaneTarget.classList.add("hidden");
      this.element.disabled = false;
    }
  }

  showNewLabelForm(): void {
    this.newLabelFormTarget.classList.remove("hidden");
    this.labelListTarget.classList.add("hidden");
  }

  async submitNewLabel(event: SubmitEvent): Promise<void> {
    event.preventDefault();

    const form = this.newLabelFormTarget;
    const submitButton = event.submitter as HTMLButtonElement;

    if (!form) {
      return;
    }

    this.newLabelErrorTarget.textContent = "";
    submitButton.disabled = true;

    const submitUrl = form.action;
    const formData = new FormData(form);

    const response = await fetch(submitUrl, {
      method: "POST",
      body: formData,
      headers: {
        Accept: "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok) {
      this.newLabelErrorTarget.textContent =
        "There was an error creating the label, please check your label name and try again.";
      submitButton.disabled = false;
      return;
    }

    // Trigger the workflow action by programmatically clicking a hidden label button.
    // This approach is a bit hacky but it avoids manually replicating the
    // PATCH request logic inside this controller.
    if (this.newLabelButtonTarget && data.uuid) {
      this.newLabelButtonTarget.value = data.uuid;
      this.newLabelButtonTarget.click();
    }

    submitButton.disabled = false;
  }
}
